import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Images, Maximize2, X } from 'lucide-react';
import type { TrinityCardSlot } from '../../services/directorApi';
import type { ModelConfig } from '../../../../services/api';

interface Props {
  open: boolean;
  slot: TrinityCardSlot;
  cardLabel: string;
  prompt: string | null;
  referenceCount: number;
  estimatedCredits: number;
  currentCredits: number;
  models: ModelConfig[];
  defaultModelId: string;
  defaultQuality: 'low' | 'medium' | 'high';
  onConfirm: (config: { modelId: string; quality: 'low' | 'medium' | 'high' }) => void;
  onClose: () => void;
}

const STORAGE_KEY = 'trinity-image-config';

function loadStoredConfig(): { modelId?: string; quality?: 'low' | 'medium' | 'high' } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

function saveStoredConfig(config: { modelId: string; quality: 'low' | 'medium' | 'high' }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
}

export function TrinityImageGenerateDialog({
  open,
  slot: _slot,
  cardLabel,
  prompt,
  referenceCount,
  estimatedCredits,
  currentCredits,
  models,
  defaultModelId,
  defaultQuality,
  onConfirm,
  onClose,
}: Props) {
  const stored = useMemo(() => loadStoredConfig(), []);
  const [modelId, setModelId] = useState(stored?.modelId || defaultModelId);
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>(
    stored?.quality || defaultQuality
  );

  useEffect(() => {
    if (open) {
      const s = loadStoredConfig();
      setModelId(s?.modelId || defaultModelId);
      setQuality(s?.quality || defaultQuality);
    }
  }, [open, defaultModelId, defaultQuality]);

  const selectedModel = models.find((m) => m.id === modelId) || models[0];
  const qualityCredits = selectedModel?.credits?.[quality] ?? estimatedCredits;
  const canAfford = currentCredits >= qualityCredits;

  const handleConfirm = () => {
    saveStoredConfig({ modelId, quality });
    onConfirm({ modelId, quality });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">
            生成{cardLabel}图片
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          {/* 文案预览 */}
          {prompt && (
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-[11px] font-medium text-slate-500">基于文案</p>
              <p className="mt-1 line-clamp-2 text-xs text-slate-600">{prompt}</p>
            </div>
          )}

          {/* 模型选择 */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-700">图片模型</label>
            <select
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {selectedModel?.description && (
              <p className="text-[11px] text-slate-500">{selectedModel.description}</p>
            )}
          </div>

          {/* 画质选择 */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-700">画质</label>
            <div className="flex gap-2">
              {(['low', 'medium', 'high'] as const).map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuality(q)}
                  className={`flex-1 rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                    quality === q
                      ? 'border-violet-300 bg-violet-50 text-violet-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {q === 'low' ? '低' : q === 'medium' ? '中' : '高'}
                </button>
              ))}
            </div>
          </div>

          {/* 参考图 & 积分信息 */}
          <div className="space-y-2 rounded-xl bg-slate-50 p-3 text-sm">
            <div className="flex items-center gap-1.5 text-slate-600">
              <Images className="h-3.5 w-3.5" />
              <span>将使用 {referenceCount} 张参考图</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-600">
              <Maximize2 className="h-3.5 w-3.5" />
              <span>输出尺寸：16:9 / 1792×1024（固定）</span>
            </div>
            <div className="flex items-center justify-between text-slate-600">
              <span>图片积分</span>
              <span className="font-medium text-slate-900">{qualityCredits}</span>
            </div>
            <div className="flex items-center justify-between text-slate-600">
              <span>当前余额</span>
              <span className="font-medium text-slate-900">{currentCredits}</span>
            </div>
            {!canAfford && (
              <div className="flex items-center gap-1 text-rose-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span className="text-xs">积分不足，请先充值</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canAfford}
            className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            确认生成
          </button>
        </div>
      </div>
    </div>
  );
}
